import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

/* ── Design tokens (V6 aligned) ─────────────────────────────────────────── */
const navy    = '#0D2137';
const teal    = '#1B6B72';
const gold    = '#C9A84C';
const goldS   = '#e8d49a';
const paper   = '#F8F6F0';
const paper2  = '#faf7f1';
const paper3  = '#fffdf9';
const cream   = '#F5F0E8';
const bone    = '#DFD9CB';
const ink     = '#0D1F2C';
const muted   = '#5c6a73';
const faint   = '#8e9ba3';
const darkBar = '#071522';
const darkBar2= '#102B45';

const SERIF = "'DM Serif Display', Georgia, serif";
const SANS  = "'DM Sans', system-ui, sans-serif";

const FR_AUDIO_URL = 'https://customer-assets-0z36b82j.emergentagent.net/job_c5fad414-0b45-4dfc-a09d-4f26483de910/artifacts/ihdv5kbe_fr_round%20_sample.mp3';

const IK = 'https://ik.imagekit.io/enrollforge';

/* ── Waveform bar heights ────────────────────────────────────────────────── */
const WAVEFORM_BARS = [13,18,22,16,28,20,26,15,23,19,17,25,21,14,27,23,17,25,19,21,15,22,17,20,26];

/* ── Static data ─────────────────────────────────────────────────────────── */
const STATS = [
  { val: '3 MIN',   lbl: 'Average Length'  },
  { val: '5',       lbl: 'Stories per Episode' },
  { val: 'DAILY',   lbl: 'Every Weekday'   },
  { val: '21 DAYS', lbl: 'Listening Window' },
];

const EPISODES = [
  { ep: 'EP. 184', date: 'May 20, 2026',  title: 'Markets Rally as Inflation Cools',               dur: '3:00', stories: 5 },
  { ep: 'EP. 183', date: 'May 19, 2026',  title: 'Treasury Yields Fall as Growth Signals Weaken',  dur: '3:00', stories: 5 },
  { ep: 'EP. 182', date: 'May 16, 2026',  title: "Jobs Data Surprises Higher — What's Next",       dur: '3:00', stories: 6 },
  { ep: 'EP. 181', date: 'May 15, 2026',  title: 'AI Investment Heats Up Across Enterprise',       dur: '3:00', stories: 4 },
  { ep: 'EP. 180', date: 'May 14, 2026',  title: "Policy Watch: What Passed, What's Next",         dur: '3:00', stories: 4 },
];

const HOW_STEPS = [
  { num: '01', label: 'Bell rings',      desc: 'A distinctive signal opens the brief and sets the tone.' },
  { num: '02', label: 'Curated stories', desc: '5 essential stories covering business, policy, and opportunity.' },
  { num: '03', label: '3 minutes',       desc: 'Designed to be finished before your coffee cools.' },
  { num: '04', label: 'One action',      desc: 'Every episode ends with something concrete you can do today.' },
];

const TESTIMONIALS = [
  { name: 'Sarah L.',  role: 'Head of Strategy',   quote: "First Round is my non-negotiable every morning. It's fast, smart, and always actionable." },
  { name: 'James T.',  role: 'Portfolio Manager',  quote: 'Three minutes that save me an hour. The takeaway is always the part I need.' },
  { name: 'Priya K.',  role: 'Policy Advisor',     quote: 'Clear, unbiased, and incredibly useful. I share it with my whole team.' },
];

/* ── Sub-components ──────────────────────────────────────────────────────── */

/* Clock-O wordmark glyph — unchanged */
const ClockO = ({ size }) => (
  <svg viewBox="0 0 140 140" width={size} height={size} aria-hidden="true"
    style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
    <circle cx="70" cy="70" r="62" fill="none" stroke={navy} strokeWidth="13"/>
    <path d="M70 70 L70 16 A54 54 0 0 1 117 43 Z" fill={gold}/>
    <line x1="70" y1="70" x2="70" y2="32" stroke={navy} strokeWidth="7" strokeLinecap="round"/>
    <line x1="70" y1="70" x2="99" y2="84" stroke={navy} strokeWidth="7" strokeLinecap="round"/>
    <circle cx="70" cy="70" r="8" fill={navy}/>
  </svg>
);

/* Mini clock for cover art */
const MiniClock = () => (
  <svg viewBox="0 0 60 60" width="21" height="21" style={{ display:'inline-block', verticalAlign:'middle' }}>
    <circle cx="30" cy="30" r="26" fill="none" stroke="#fff" strokeWidth="5.5"/>
    <path d="M30 30 L30 8 A22 22 0 0 1 49 19 Z" fill={gold}/>
    <line x1="30" y1="30" x2="30" y2="13" stroke="#fff" strokeWidth="3.5" strokeLinecap="round"/>
    <line x1="30" y1="30" x2="41" y2="36" stroke="#fff" strokeWidth="3.5" strokeLinecap="round"/>
    <circle cx="30" cy="30" r="3.5" fill="#fff"/>
  </svg>
);

/* Motion lines below FIRST — unchanged */
const MotionLines = () => (
  <svg viewBox="0 0 220 18" width="220" height="18" aria-hidden="true"
    style={{ display:'block', margin:'7px 0 5px' }}>
    {[0,7,14].map((y, i) => (
      <line key={y} x1={i*14} y1={y+2} x2={220-i*20} y2={y+2}
        stroke={gold} strokeWidth="2.5" strokeLinecap="round" opacity={1-i*0.28}/>
    ))}
  </svg>
);

/* How-it-Works arrow */
const WorksArrow = () => (
  <div className="fr-arrow" style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'0 4px', alignSelf:'center' }}>
    <svg viewBox="0 0 28 24" width="28" height="24" fill="none">
      <line x1="3" y1="12" x2="22" y2="12" stroke={bone} strokeWidth="2" strokeLinecap="round"/>
      <polyline points="16,6 22,12 16,18" stroke={bone} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </div>
);

/* Check badge */
const Check = ({ light = false }) => (
  <svg viewBox="0 0 20 20" width="15" height="15" fill="none" style={{ flexShrink:0 }}>
    <circle cx="10" cy="10" r="9" stroke={light ? goldS : teal} strokeWidth="1.5"/>
    <polyline points="6,10 9,13 14,7" stroke={light ? goldS : teal} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* PlayBtn for episode rows */
const PlayBtn = ({ size = 32, goldFill = false }) => (
  <button disabled aria-label="Play episode (coming soon)" style={{
    width: size, height: size, borderRadius: '50%',
    border: `1.5px solid ${goldFill ? gold : bone}`,
    background: goldFill ? gold : '#fff',
    display:'flex', alignItems:'center', justifyContent:'center',
    cursor:'not-allowed', flexShrink:0,
  }}>
    <svg viewBox="0 0 16 16" width="10" height="10" fill={goldFill ? '#fff' : navy}>
      <polygon points="4,2 14,8 4,14"/>
    </svg>
  </button>
);

/* Apple App Store icon SVG */
const AppleIcon = () => (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="white" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

/* Google Play icon SVG */
const GooglePlayIcon = () => (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="white" aria-hidden="true">
    <path d="M3,20.5v-17c0-0.83,0.94-1.3,1.6-0.8l14,8.5c0.6,0.37,0.6,1.23,0,1.6l-14,8.5C3.94,21.8,3,21.33,3,20.5z"/>
  </svg>
);

/* ══════════════════════════════════════════════════════════════════════════════
   PAGE
══════════════════════════════════════════════════════════════════════════════ */
export default function FirstRoundPage() {
  const [samplePlaying, setSamplePlaying] = useState(false);
  const sampleAudioRef                    = useRef(null);

  useEffect(() => {
    const audio = new Audio(FR_AUDIO_URL);
    sampleAudioRef.current = audio;
    const onEnded = () => { setSamplePlaying(false); audio.currentTime = 0; };
    audio.addEventListener('ended', onEnded);
    return () => { audio.pause(); audio.removeEventListener('ended', onEnded); };
  }, []);

  const toggleSample = () => {
    const audio = sampleAudioRef.current;
    if (!audio) return;
    if (samplePlaying) { audio.pause(); setSamplePlaying(false); }
    else               { audio.play();  setSamplePlaying(true);  }
  };


  return (
    <div className="fr-page" data-testid="first-round-page">
      <div className="fr-shell">

        {/* ═══════════════════════════════════════
            1. OPENING — Wordmark + Master Player
        ═══════════════════════════════════════ */}
        <section style={{ background: paper, position:'relative', overflow:'hidden', padding:'72px 0 64px', borderBottom:`1px solid ${bone}` }}>
          {/* Subtle radial gradient background */}
          <div style={{ position:'absolute', inset:0, background:'radial-gradient(ellipse at 20% 60%, rgba(201,168,76,.12) 0%, transparent 50%), radial-gradient(ellipse at 80% 30%, rgba(27,107,114,.1) 0%, transparent 55%)', pointerEvents:'none' }} />

          <div className="fr-wrap" style={{ position:'relative' }}>
            <div className="fr-hero-grid">

              {/* Left: branding + CTAs */}
              <div>
                {/* Badge */}
                <div style={{ display:'inline-flex', alignItems:'center', gap:7, background:'rgba(27,107,114,.08)', border:`1px solid rgba(27,107,114,.18)`, borderRadius:999, padding:'5px 14px', fontSize:11, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase', color:teal, marginBottom:22, fontFamily:SANS }}>
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.5"/><circle cx="6" cy="6" r="2" fill="currentColor"/></svg>
                  The 3-Minute Daily Briefing · Opportunity Post
                </div>

                {/* Wordmark — FIRST R[clock]UND */}
                <div style={{ fontSize:'clamp(60px, 8.5vw, 100px)', fontWeight:900, letterSpacing:'-0.04em', lineHeight:0.88, color:navy, fontFamily:SANS }}>
                  FIRST
                </div>
                <MotionLines />
                <div style={{ display:'flex', alignItems:'center', lineHeight:0.88, marginTop:-2 }}>
                  <span style={{ fontSize:'clamp(60px, 8.5vw, 100px)', fontWeight:900, letterSpacing:'-0.04em', color:navy, fontFamily:SANS, marginRight:2 }}>R</span>
                  <ClockO size={Math.round(Math.min(100, Math.max(60, 100 * 0.9)))} />
                  <span style={{ fontSize:'clamp(60px, 8.5vw, 100px)', fontWeight:900, letterSpacing:'-0.04em', color:navy, fontFamily:SANS, marginLeft:2 }}>UND</span>
                </div>

                <div style={{ marginTop:20, fontSize:'clamp(10px, 1.1vw, 12.5px)', fontWeight:800, letterSpacing:'0.17em', textTransform:'uppercase', color:navy, fontFamily:SANS }}>
                  START YOUR DAY WITH A WIN.
                </div>

                {/* Sub copy */}
                <p style={{ marginTop:14, fontSize:15, lineHeight:1.68, color:muted, maxWidth:400, fontFamily:SANS }}>
                  Each morning, First Round delivers <strong style={{ color:ink }}>5 handpicked stories</strong> in just 3 minutes — plus one <strong style={{ color:ink }}>actionable takeaway</strong> to help you move first.
                </p>

                {/* CTAs */}
                <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginTop:22, marginBottom:20 }}>
                  <Link
                    to="/subscribe"
                    style={{ display:'inline-flex', alignItems:'center', gap:7, background:teal, color:'#fff', padding:'13px 26px', borderRadius:10, fontFamily:SANS, fontWeight:700, fontSize:14, letterSpacing:'.01em', textDecoration:'none', transition:'background .2s' }}
                    data-testid="first-round-cta-primary"
                  >
                    Get First Round Free →
                  </Link>
                  <button
                    onClick={toggleSample}
                    style={{ display:'inline-flex', alignItems:'center', gap:7, background:'transparent', color:navy, border:`1.5px solid ${bone}`, padding:'12px 22px', borderRadius:10, fontFamily:SANS, fontWeight:600, fontSize:14, cursor:'pointer', transition:'all .2s' }}
                    data-testid="first-round-cta-sample"
                  >
                    {samplePlaying
                      ? <svg viewBox="0 0 16 16" width="11" height="11" fill={navy}><rect x="3" y="2" width="3.5" height="12"/><rect x="9.5" y="2" width="3.5" height="12"/></svg>
                      : <svg viewBox="0 0 16 16" width="11" height="11" fill={navy}><polygon points="4,2 14,8 4,14"/></svg>
                    }
                    {samplePlaying ? 'Pause Sample' : 'Listen to a Sample'}
                  </button>
                </div>

                {/* Trust checks */}
                <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
                  {['100% Free for members', 'Takes 3 minutes', 'Cancel anytime'].map(l => (
                    <div key={l} style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <Check />
                      <span style={{ fontSize:13, color:muted, fontFamily:SANS }}>{l}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Master Audio Player card */}
              <div className="fr-player-card" data-testid="first-round-master-player">
                <div className="fr-player-kicker">
                  <span className="fr-player-live-dot" />
                  Today's Episode
                </div>

                <div className="fr-player-ep-meta">EP. 184 · MAY 20, 2026</div>
                <div className="fr-player-ep-title">Markets Rally as Inflation Cools</div>

                {/* Waveform */}
                <div className="fr-player-waveform" data-testid="fr-waveform">
                  {WAVEFORM_BARS.map((h, i) => (
                    <div
                      key={i}
                      className={`fr-wf-bar${samplePlaying && i < Math.floor(WAVEFORM_BARS.length * 0.28) ? ' fr-wf-active' : ''}`}
                      style={{ height: h }}
                    />
                  ))}
                </div>
                <div className="fr-player-time">
                  <span>0:00</span>
                  <span>3:00</span>
                </div>

                {/* Play/Pause */}
                <button
                  className="fr-player-play-btn"
                  onClick={toggleSample}
                  data-testid="fr-player-play-pause"
                >
                  {samplePlaying
                    ? <svg viewBox="0 0 16 16" width="12" height="12" fill="#fff"><rect x="3" y="2" width="3.5" height="12"/><rect x="9.5" y="2" width="3.5" height="12"/></svg>
                    : <svg viewBox="0 0 16 16" width="12" height="12" fill="#fff"><polygon points="4,2 14,8 4,14"/></svg>
                  }
                  {samplePlaying ? 'Pause Sample' : 'Play Sample'}
                </button>

                {/* Stats row */}
                <div className="fr-player-stats-row">
                  {[['3:00','Duration'],['5','Stories'],['1','Takeaway']].map(([v,l]) => (
                    <div key={l} className="fr-player-stat">
                      <strong>{v}</strong>
                      {l}
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            2. TRUST BAR
        ═══════════════════════════════════════ */}
        <div className="fr-trust-bar" data-testid="first-round-trust-bar">
          <div className="fr-wrap">
            <div className="fr-trust-inner">
              {STATS.map(s => (
                <div key={s.val} className="fr-trust-item">
                  <span className="fr-trust-num">{s.val}</span>
                  <span className="fr-trust-label">{s.lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════
            3. TODAY'S BRIEFING
        ═══════════════════════════════════════ */}
        <section className="fr-briefing-section" data-testid="first-round-latest-episode">
          <div className="fr-wrap">
            <div className="fr-section-header">
              <div>
                <div className="fr-kicker">Latest Episode</div>
                <h2 className="fr-section-title">Today's Briefing</h2>
              </div>
              <Link to="/subscribe" style={{ fontSize:13, fontWeight:600, color:teal, textDecoration:'none', fontFamily:SANS }}>
                View All Episodes →
              </Link>
            </div>

            <div className="fr-briefing-card">
              {/* Cover art */}
              <div className="fr-briefing-cover">
                <div style={{ fontSize:16, fontWeight:900, letterSpacing:'-0.03em', color:'#fff', fontFamily:SANS, lineHeight:0.88 }}>FIRST</div>
                <div style={{ width:28, height:2, background:gold, margin:'5px auto' }} />
                <div style={{ display:'flex', alignItems:'center', gap:1 }}>
                  <span style={{ fontSize:16, fontWeight:900, color:'#fff', fontFamily:SANS, lineHeight:0.88, marginRight:2 }}>R</span>
                  <MiniClock />
                  <span style={{ fontSize:16, fontWeight:900, color:'#fff', fontFamily:SANS, lineHeight:0.88, marginLeft:2 }}>UND</span>
                </div>
                <div style={{ fontSize:8, fontWeight:700, letterSpacing:'0.09em', color:faint, textTransform:'uppercase', marginTop:10, fontFamily:SANS, lineHeight:1.5, maxWidth:120, textAlign:'center' }}>
                  THE 3-MINUTE DAILY BRIEFING<br/>FROM OPPORTUNITY POST.
                </div>
              </div>

              {/* Episode info */}
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:11 }}>
                  <div style={{ width:3, height:14, background:gold, borderRadius:2 }} />
                  <span style={{ fontSize:9.5, fontWeight:800, letterSpacing:'0.16em', textTransform:'uppercase', color:gold, fontFamily:SANS }}>LATEST EPISODE</span>
                </div>
                <div style={{ fontSize:11, fontWeight:600, letterSpacing:'0.05em', color:faint, marginBottom:9, fontFamily:SANS, textTransform:'uppercase' }}>
                  MAY 20, 2026 &nbsp;·&nbsp; EPISODE 184
                </div>
                <div style={{ fontSize:'clamp(16px, 1.8vw, 22px)', fontWeight:400, color:'#fff', fontFamily:SERIF, lineHeight:1.25, marginBottom:11 }}>
                  Markets Rally as Inflation Cools
                </div>
                <p style={{ fontSize:13, color:'rgba(240,237,231,.55)', lineHeight:1.62, marginBottom:18, maxWidth:400, fontFamily:SANS }}>
                  Cooling prices, easing yield pressures, and what today's moves mean for your next 90 days.
                </p>
                <div style={{ display:'flex', gap:20, alignItems:'center' }}>
                  {[['3:00',''],['5','STORIES'],['1','TAKEAWAY']].map(([v,l]) => (
                    <div key={l} style={{ display:'flex', alignItems:'baseline', gap:4 }}>
                      <span style={{ fontSize:13, fontWeight:800, color:'#fff', fontFamily:SANS }}>{v}</span>
                      {l && <span style={{ fontSize:9.5, fontWeight:600, color:faint, fontFamily:SANS, textTransform:'uppercase', letterSpacing:'0.06em' }}>{l}</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Player column */}
              <div style={{ display:'flex', flexDirection:'column', alignItems:'stretch', gap:14 }}>
                <button
                  onClick={toggleSample}
                  style={{ background:gold, color:'#fff', border:'none', borderRadius:8, padding:'12px 0', fontFamily:SANS, fontWeight:800, fontSize:12, letterSpacing:'0.06em', textTransform:'uppercase', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:7, transition:'opacity .15s' }}
                  data-testid="briefing-play-btn"
                >
                  {samplePlaying
                    ? <><svg viewBox="0 0 16 16" width="10" height="10" fill="#fff"><rect x="3" y="2" width="3.5" height="12"/><rect x="9.5" y="2" width="3.5" height="12"/></svg> PAUSE</>
                    : <><svg viewBox="0 0 16 16" width="10" height="10" fill="#fff"><polygon points="4,2 14,8 4,14"/></svg> LISTEN NOW</>
                  }
                </button>
                <div style={{ borderTop:`1px solid rgba(255,255,255,.1)`, borderBottom:`1px solid rgba(255,255,255,.1)`, padding:'10px 0' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:2.5, height:30, justifyContent:'center' }}>
                    {WAVEFORM_BARS.slice(0,22).map((h, i) => (
                      <div key={i} style={{ width:3, borderRadius:2, height:h, background: i < 6 && samplePlaying ? gold : 'rgba(255,255,255,.18)' }}/>
                    ))}
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginTop:6 }}>
                    <span style={{ fontSize:9.5, color:faint, fontFamily:SANS }}>0:00</span>
                    <span style={{ fontSize:9.5, color:faint, fontFamily:SANS }}>3:00</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            4. HOW FIRST ROUND WORKS
        ═══════════════════════════════════════ */}
        <section className="fr-how-section" data-testid="how-it-works">
          <div className="fr-wrap">
            <div className="fr-section-header" style={{ marginBottom:28 }}>
              <div>
                <div className="fr-kicker">The Format</div>
                <h2 className="fr-section-title">How First Round Works</h2>
              </div>
            </div>
            <div className="fr-works-grid">
              {HOW_STEPS.map((item, i) => (
                <React.Fragment key={item.num}>
                  <div style={{ background:'#fff', border:`1px solid ${bone}`, borderRadius:17, padding:'24px 20px' }}>
                    <div style={{ fontSize:28, fontWeight:900, color:gold, fontFamily:SANS, lineHeight:1, marginBottom:10 }}>{item.num}</div>
                    <div style={{ fontSize:15, fontWeight:400, color:navy, fontFamily:SERIF, marginBottom:7 }}>{item.label}</div>
                    <p style={{ fontSize:13, color:muted, lineHeight:1.55, fontFamily:SANS, margin:0 }}>{item.desc}</p>
                  </div>
                  {i < HOW_STEPS.length - 1 && <WorksArrow />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            5. THIS WEEK'S BRIEFINGS
        ═══════════════════════════════════════ */}
        <section className="fr-episodes-section" data-testid="first-round-episode-list">
          <div className="fr-wrap">
            <div className="fr-week-grid">
              {/* Left: episode list */}
              <div>
                <div className="fr-section-header" style={{ marginBottom:8 }}>
                  <h2 className="fr-section-title">This Week's Briefings</h2>
                  <Link to="/subscribe" style={{ fontSize:12, fontWeight:600, letterSpacing:'0.04em', color:teal, textDecoration:'none', fontFamily:SANS }}>
                    View all episodes →
                  </Link>
                </div>
                <p style={{ fontSize:13, color:muted, marginBottom:16, fontFamily:SANS }}>
                  Listen to the last 21 days. Older episodes move to the archive.
                </p>
                <div style={{ border:`1px solid ${bone}`, borderRadius:17, overflow:'hidden', background:'#fff' }}>
                  {EPISODES.map((ep, i) => (
                    <div
                      key={ep.ep}
                      style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 18px', borderBottom: i < EPISODES.length-1 ? `1px solid ${paper2}` : 'none', background: i===0 ? '#fdfaf4' : '#fff' }}
                      data-testid={`episode-row-${i}`}
                    >
                      <PlayBtn goldFill={i===0} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:10, fontWeight:700, letterSpacing:'0.07em', color:faint, textTransform:'uppercase', fontFamily:SANS, marginBottom:3, display:'flex', alignItems:'center', gap:7, flexWrap:'wrap' }}>
                          <span>{ep.date} &nbsp;·&nbsp; {ep.ep}</span>
                          {i===0 && <span style={{ background:gold, color:'#fff', fontSize:8.5, fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase', padding:'2px 7px', borderRadius:20, fontFamily:SANS }}>LATEST</span>}
                        </div>
                        <div style={{ fontSize:14, fontWeight:400, color:navy, fontFamily:SERIF, lineHeight:1.3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {ep.title}
                        </div>
                      </div>
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:3, flexShrink:0 }}>
                        <span style={{ fontSize:12, fontWeight:700, color:navy, fontFamily:SANS }}>{ep.dur}</span>
                        <span style={{ fontSize:10, color:muted, fontFamily:SANS, textTransform:'uppercase', letterSpacing:'0.04em' }}>{ep.stories} STORIES</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: 21-day access card */}
              <div style={{ background:'#0D2137', borderRadius:22, padding:'28px 24px' }} data-testid="first-round-21-day-card">
                <div style={{ fontSize:9.5, fontWeight:800, letterSpacing:'0.14em', textTransform:'uppercase', color:faint, fontFamily:SANS, marginBottom:9 }}>21-DAY ACCESS</div>
                <div style={{ fontSize:20, fontWeight:400, color:'#fff', fontFamily:SERIF, marginBottom:16, lineHeight:1.2 }}>
                  Members get full access to the latest 21 days.
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:13, marginBottom:22 }}>
                  {[{ h:'Stay current', d:'Never miss a morning.' },{ h:'Revisit & act', d:'Catch up and take action when it matters.' },{ h:'Older episodes', d:'Episodes older than 21 days move to the archive.' }].map(item => (
                    <div key={item.h}>
                      <div style={{ fontSize:13, fontWeight:600, color:'#fff', fontFamily:SANS, marginBottom:2 }}>{item.h}</div>
                      <div style={{ fontSize:12, color:faint, fontFamily:SANS, lineHeight:1.4 }}>{item.d}</div>
                    </div>
                  ))}
                </div>
                <Link to="/subscribe" style={{ display:'block', textAlign:'center', background:'rgba(255,255,255,.06)', border:`1px solid ${gold}`, color:goldS, padding:'11px 0', borderRadius:8, fontSize:12, fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase', textDecoration:'none', fontFamily:SANS }}>
                  EXPLORE THE ARCHIVE
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            6. WHY LISTEN — Testimonials
        ═══════════════════════════════════════ */}
        <section className="fr-reactions-section" data-testid="first-round-testimonials">
          <div className="fr-wrap">
            <div className="fr-section-header" style={{ marginBottom:28 }}>
              <div>
                <div className="fr-kicker">Member Reactions</div>
                <h2 className="fr-section-title">What First Round readers say.</h2>
              </div>
              <Link to="/subscribe" style={{ fontSize:12, fontWeight:600, letterSpacing:'0.04em', color:teal, textDecoration:'none', fontFamily:SANS }}>
                Join the conversation →
              </Link>
            </div>
            <div className="fr-reactions-grid">
              {TESTIMONIALS.map((t, i) => (
                <div key={t.name} style={{ background:'#fff', border:`1px solid ${bone}`, borderRadius:17, padding:'22px 20px 18px' }}>
                  <div style={{ width:36, height:36, borderRadius:'50%', background: i===0 ? `linear-gradient(135deg,${teal},${gold})` : i===1 ? `linear-gradient(135deg,${navy},${teal})` : `linear-gradient(135deg,${gold},${teal})`, marginBottom:14 }} />
                  <p style={{ fontSize:14.5, fontStyle:'italic', color:muted, fontFamily:SERIF, lineHeight:1.58, marginBottom:16, marginTop:0 }}>
                    "{t.quote}"
                  </p>
                  <div style={{ borderTop:`1px solid ${bone}`, paddingTop:12 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:navy, fontFamily:SANS }}>{t.name}</div>
                    <div style={{ fontSize:12, color:faint, fontFamily:SANS, marginTop:2 }}>{t.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            7. MOBILE APP DOWNLOAD
        ═══════════════════════════════════════ */}
        <section className="fr-app-section" data-testid="first-round-app-section">
          <div className="fr-wrap">
            <div className="fr-app-card">
              {/* Left: messaging + store buttons */}
              <div>
                <div className="fr-app-badge">
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.5"/><circle cx="6" cy="6" r="2" fill="currentColor"/></svg>
                  Coming Fall 2026
                </div>
                <h2>Take First Round<br /><em>with you.</em></h2>
                <p>Download the First Round app and get your daily 3-minute briefing wherever you are — your commute, your coffee, your morning run.</p>

                <div className="fr-app-store-btns">
                  <a href="#" className="fr-app-store-btn" aria-label="Download on the App Store (coming soon)" onClick={e => e.preventDefault()}>
                    <span className="fr-app-store-icon"><AppleIcon /></span>
                    <span className="fr-app-store-text">
                      <span className="fr-app-store-sub">Download on the</span>
                      <span className="fr-app-store-name">App Store</span>
                    </span>
                    <span className="fr-app-store-tag">Soon</span>
                  </a>
                  <a href="#" className="fr-app-store-btn" aria-label="Get it on Google Play (coming soon)" onClick={e => e.preventDefault()}>
                    <span className="fr-app-store-icon"><GooglePlayIcon /></span>
                    <span className="fr-app-store-text">
                      <span className="fr-app-store-sub">Get it on</span>
                      <span className="fr-app-store-name">Google Play</span>
                    </span>
                    <span className="fr-app-store-tag">Soon</span>
                  </a>
                </div>
              </div>

              {/* Right: QR code placeholders */}
              <div className="fr-app-qr-col">
                <div className="fr-qr-card">
                  <div className="fr-qr-placeholder">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.3)" strokeWidth="1.5">
                      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="3" height="3"/>
                      <rect x="19" y="19" width="2" height="2"/><rect x="14" y="19" width="2" height="2"/>
                      <rect x="19" y="14" width="2" height="2"/>
                    </svg>
                  </div>
                  <div className="fr-qr-label">iOS QR</div>
                </div>
                <div className="fr-qr-card">
                  <div className="fr-qr-placeholder">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.3)" strokeWidth="1.5">
                      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="3" height="3"/>
                      <rect x="19" y="19" width="2" height="2"/><rect x="14" y="19" width="2" height="2"/>
                      <rect x="19" y="14" width="2" height="2"/>
                    </svg>
                  </div>
                  <div className="fr-qr-label">Android QR</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            8. NEWSLETTER CTA (V6 card)
        ═══════════════════════════════════════ */}
        <section className="fr-nl-section" data-testid="first-round-claim-band">
          <div className="fr-wrap">
            <div className="fr-nl-card">
              <div>
                <div className="fr-nl-eye">First Round</div>
                <h2>Claim your morning <em>advantage.</em></h2>
                <p>Free. No spam. Just 3 minutes of intelligence that pays — every weekday morning.</p>

                <Link
                  to="/subscribe?product=first_round"
                  className="fr-nl-cta-btn"
                  data-testid="first-round-signup-cta"
                  style={{ display:'inline-flex', alignItems:'center', gap:7, background:teal, color:'#fff', padding:'14px 28px', borderRadius:10, fontFamily:SANS, fontWeight:700, fontSize:14, letterSpacing:'.01em', textDecoration:'none', marginTop:8 }}
                >
                  Get First Round Free →
                </Link>

                <div style={{ display:'flex', gap:22, marginTop:16, flexWrap:'wrap' }}>
                  {['100% Free','Cancel anytime','Takes 3 minutes'].map(l => (
                    <div key={l} style={{ display:'flex', alignItems:'center', gap:7 }}>
                      <Check light />
                      <span className="fr-nl-fine" style={{ marginBottom:0 }}>{l}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="fr-nl-visual" />
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            9. SPONSOR STRIP
        ═══════════════════════════════════════ */}
        <div style={{ background: cream, borderTop:`1px solid ${bone}`, padding:'22px 0' }}>
          <div className="fr-wrap" style={{ display:'flex', flexDirection:'column', gap:12, alignItems:'center' }}>
            <span style={{ fontSize:10, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'#b0a08a', fontFamily:SANS, textAlign:'center' }}>
              PRESENTED BY OPPORTUNITY POST — PROUDLY SPONSORED BY LEADERS WHO INVEST IN OPPORTUNITY.
            </span>
            <div style={{ display:'flex', gap:'clamp(14px, 2.8vw, 34px)', justifyContent:'center', flexWrap:'wrap' }}>
              {['JPMorgan Chase','Microsoft','Goldman Sachs','AWS','Deloitte','VISA'].map(s => (
                <span key={s} style={{ fontSize:12, fontWeight:700, color:'#c0b49a', fontFamily:SANS, letterSpacing:'0.04em' }}>{s}</span>
              ))}
            </div>
          </div>
        </div>

      </div>{/* /fr-shell */}
    </div>
  );
}
